import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  CardContent,
  Stack,
  Avatar,
  Divider,
  Button,
  MenuItem,
  TextField,
  IconButton,
} from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import errorHandler from "../../utils/errorHandler";
import CardLayout from "../../layouts/CardLayout";
import { IMAGE_BASE } from "../../utils/constants";
import ApiService from "../../services/categoryManagementApi";
import { toast } from "react-toastify";

const { productManagementApi, dealerManagementApi } = ApiService;

const ProductView = () => {
  const { id } = useParams();
  const [data, setData] = useState({});
  const [showPriceHistory, setShowPriceHistory] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [dealerAssignments, setDealerAssignments] = useState([]);
  const [showDealerStock, setShowDealerStock] = useState(false);
  const [dealerStockEntries, setDealerStockEntries] = useState([{ dealerId: "", quantity: "" }]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await productManagementApi.getById({ id: id });
        setData(response?.data?.data);
      } catch (error) {
        errorHandler(error);
      }
    };

    fetchData();
  }, [id]);

  const fetchDealers = async () => {
    try {
      const body = {
        searchItem: "",
        pageNumber: 1,
        pageSize: 100,
        status: [],
      };

      const dealerRes = await dealerManagementApi.getAll(body);
      setDealers(dealerRes?.data?.data?.result || []);
    } catch (error) {
      errorHandler(error);
    }
  };

  const fetchDealerAssignments = async () => {
  try {
    const quantityRes = await productManagementApi.getProductStock({ productId: id });
    const assignments = quantityRes?.data?.data || [];

    setDealerAssignments(assignments);

    if (assignments.length > 0) {
      setDealerStockEntries(
        assignments.map((a) => ({
          dealerId: a.dealerId?._id,  
          quantity: a.qty?.toString(), 
        }))
      );
    } else {
      setDealerStockEntries([{ dealerId: "", quantity: "" }]);
    }
  } catch (error) {
    errorHandler(error);
  }
};


  const handleToggleDealerStock = () => {
    if (!showDealerStock) {
      fetchDealers();
      fetchDealerAssignments();
    }
    setShowDealerStock(!showDealerStock);
  };

  const handleAddDealerEntry = () => {
    setDealerStockEntries((prev) => [...prev, { dealerId: "", quantity: "" }]);
  };

  const handleRemoveDealerEntry = (index) => {
    setDealerStockEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDealerEntryChange = (index, field, value) => {
    setDealerStockEntries((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry))
    );
  };

  const handleSaveDealerStocks = async () => {
  try {
    const payload = {
      productId: id,
      dealers: dealerStockEntries
        .filter((entry) => entry.dealerId && entry.quantity !== "")
        .map((entry) => ({
          dealerId: entry.dealerId,
          qty: Number(entry.quantity),
        })),
    };

    const res = await productManagementApi.stockUpdate(payload);
    console.log("res",res);
    if(res?.status == 200){
      toast.success(res?.data?.message || "Stock Update Successfully", {toastId : "success"})
      
      await fetchDealerAssignments();
    }
  } catch (error) {
    errorHandler(error);
  }
};


  const InfoItem = ({ label, value }) => (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h6" color="text.primary" sx={{ wordBreak: "break-word" }}>
        {value || "-"}
      </Typography>
    </Box>
  );

  const totalQuantity = Number(data?.qty || 0);
  const assignedQuantity = dealerStockEntries.reduce(
    (sum, entry) => sum + Number(entry.quantity || 0),
    0
  );

  const remainingQuantity = totalQuantity - assignedQuantity;


  return (
    <Box>
      <Helmet>
        <title>Product View</title>
      </Helmet>

      <CardLayout>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">View Product Details</Typography>
        </Box>

        <Card
          sx={{
            boxShadow: 6,
            borderRadius: 4,
            background: "linear-gradient(145deg, #f0f0f0, #ffffff)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 4,
              background: "linear-gradient(135deg, #f3f54d, #00a1dd)",
              textAlign: "center",
              color: "#fff",
            }}
          >
            <Avatar
              src={IMAGE_BASE + data?.image}
              alt="Product Image"
              sx={{
                width: 120,
                height: 120,
                mx: "auto",
                mb: 2,
                border: "4px solid #fff",
                boxShadow: 3,
              }}
            />
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {data?.name || "-"}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {data?.nameKh || "-"}
            </Typography>
          </Box>

          <CardContent>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                  <InfoItem label="Category" value={data?.categoryId?.name} />
                  <InfoItem label="Price" value={data?.price ? `$${data.price}` : "-"} />
                  <InfoItem label="Quantity" value={data?.qty} />
                  <InfoItem label="Product Position" value={data?.index} />

                  {data?.weight && <InfoItem label="Weight" value={data?.weight} />}
                  {data?.redeemRewardPoint && (
                    <InfoItem label="Reward Points" value={data?.redeemRewardPoint} />
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                  <InfoItem label="Product ID" value={data?.productId} />
                  <InfoItem label="Product Model" value={data?.productModel} />
                  <InfoItem label="Description" value={data?.description} />
                </Stack>
              </Grid>
            </Grid>

            <Box mt={3} textAlign="center" display="flex" justifyContent="center" gap={2} >
              <Button
                variant="contained"
                color="primary"
                onClick={() => setShowPriceHistory((prev) => !prev)}
              >
                {showPriceHistory ? "Hide Price History" : "Show Price History"}
              </Button>

              <Button variant="contained" color="secondary" onClick={handleToggleDealerStock}>
                {showDealerStock ? "Hide Dealer Stock" : "Add Dealer Stock"}
              </Button>
            </Box>

            {showPriceHistory && (
              <Box mt={4}>
                <Typography variant="h5" gutterBottom>
                  Price History
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {data?.priceHistory?.length === 0 ? (
                  <Typography>No price history available.</Typography>
                ) : (
                  data?.priceHistory?.map((entry) => (
                    <Card key={entry._id} sx={{ mb: 2, p: 2, boxShadow: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <Typography>
                            <strong>Price:</strong> ${entry.price}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography>
                            <strong>Updated By:</strong> {entry.updatedBy?.name || "Unknown"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography>
                            <strong>Email</strong> {entry.updatedBy?.email || "Unknown"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography>
                            <strong>Updated At:</strong>{" "}
                            {new Date(entry.updatedAt).toLocaleString()}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Card>
                  ))
                )}
              </Box>
            )}

            {showDealerStock && (
              <Box mt={4}>
                <Typography variant="h5" gutterBottom>
                  Dealer Stock Assignments
                  {totalQuantity > 0 && (
                    <span style={{ fontSize: 16, marginLeft: 6, color: "#777" }}>
                      (Remaining: {remainingQuantity})
                    </span>
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {dealerStockEntries.map((entry, index) => (
                  <Grid
                    container
                    spacing={2}
                    key={index}
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <Grid item xs={5}>
                      <TextField
                        select
                        fullWidth
                        label="Select Dealer"
                        value={entry.dealerId}
                        onChange={(e) =>
                          handleDealerEntryChange(index, "dealerId", e.target.value)
                        }
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {dealers
                          .filter((d) => {
                            const alreadySelected = dealerStockEntries.some(
                              (e, i) => e.dealerId === d._id && i !== index
                            );
                            return !alreadySelected;
                          })
                          .map((dealer) => (
                            <MenuItem key={dealer._id} value={dealer._id}>
                              {dealer.businessName}
                            </MenuItem>
                          ))}

                      </TextField>
                    </Grid>
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        label="Quantity"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={entry.quantity}
                        onChange={(e) => {
                          const input = e.target.value;

                          let value = Number(input)
                          if (input === "") {
                            handleDealerEntryChange(index, "quantity", "");
                            return;
                          }

                          if (isNaN(value) || value < 0) {
                            toast.error("Quantity cannot be negative");
                            return;
                          }
                          const totalQuantity = Number(data?.qty || 0);

                          const assignedQuantity = dealerStockEntries.reduce(
                            (sum, entry, i) =>
                              i === index ? sum : sum + Number(entry.quantity || 0),
                            0
                          );

                          const remainingQuantity = totalQuantity - assignedQuantity;
                          if (value > remainingQuantity) {
                            toast.error(`Cannot assign more than remaining stock (${remainingQuantity})`);
                            return;
                          }

                          handleDealerEntryChange(index, "quantity", value);
                        }}

                      />
                    </Grid>
                    <Grid item xs={2} display="flex" justifyContent="center">
                      <IconButton
                        color="error"
                        disabled={dealerStockEntries.length === 1}
                        onClick={() => handleRemoveDealerEntry(index)}
                      >
                        <RemoveIcon />
                      </IconButton>
                      {index === dealerStockEntries.length - 1 && remainingQuantity > 0 && (
                        <IconButton color="primary" onClick={handleAddDealerEntry}>
                          <AddIcon />
                        </IconButton>
                      )}

                    </Grid>
                  </Grid>
                ))}

                <Box textAlign="center" mt={2}>
                  <Button variant="contained" onClick={handleSaveDealerStocks}>
                    Save Dealer Stock
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </CardLayout>
    </Box>
  );
};

export default ProductView;
